import { useRef, useEffect, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const reasons = [
  {
    icon: 'ri-focus-3-line',
    title: 'Strategic, Outcome-Focused Approach',
    desc: 'Every decision is made with your end goal in mind. We build legal strategies designed to win, not just participate.',
  },
  {
    icon: 'ri-chat-check-line',
    title: 'Clear Communication & Case Transparency',
    desc: 'You\'ll always know where your case stands. Direct, honest communication at every stage — no surprises.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Experienced Courtroom Litigators',
    desc: 'Decades of trial experience across criminal defense, civil litigation, and personal injury matters throughout California.',
  },
  {
    icon: 'ri-speed-line',
    title: 'Efficient, No-Nonsense Case Handling',
    desc: 'We move with urgency and purpose. No delays, no unnecessary complexity — just focused, decisive legal action.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Personalized, One-on-One Attention',
    desc: 'Every client works directly with an attorney. You\'re not passed off to paralegals — you get dedicated legal counsel.',
  },
];

export default function WhyChooseUs() {
  const { ref, inView } = useInView();

  return (
    <section className="bg-[#F5F3EE] py-24 md:py-32" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left: Image block */}
          <div className={`relative transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            {/* Main image */}
            <div className="relative w-full h-[560px] rounded-sm overflow-hidden">
              <img
                src="https://cdcssl.ibsrv.net/ibimg/smb/800x655_80/webmgr/21/1/4/6638d85ee7576_ThinkstockPhotos485588672_20240506_1317.jpg.webp?e09cb778a1b535665a865c7d2e1d44fe"
                alt="Gamble & Garcia Attorneys at Law"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>

            {/* Floating stat cards */}
            <div className="absolute -bottom-6 -right-4 md:-right-8 bg-[#0D0D0D] px-7 py-5 rounded-sm border border-[#C9A84C]/25">
              <div className="font-serif text-[#C9A84C] text-4xl font-bold leading-none">50+</div>
              <div className="text-white/60 text-[10px] tracking-[0.25em] uppercase mt-1">Years Combined<br />Experience</div>
            </div>
            <div className="absolute -top-5 -right-4 md:-right-8 bg-[#C9A84C] px-6 py-4 rounded-sm">
              <div className="font-serif text-black text-3xl font-bold leading-none">4.8★</div>
              <div className="text-black/60 text-[10px] tracking-[0.2em] uppercase mt-1">Google Rating</div>
            </div>

            {/* Gold vertical accent */}
            <div className="absolute left-0 top-8 bottom-8 w-[3px] bg-gradient-to-b from-transparent via-[#C9A84C] to-transparent"></div>
          </div>

          {/* Right: Content */}
          <div>
            <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-[2px] bg-[#C9A84C]"></div>
                <span className="text-[#C9A84C] text-[10px] tracking-[0.4em] uppercase font-medium">Why Choose Us</span>
              </div>
              <h2 className="font-serif text-[#111] text-4xl md:text-5xl font-bold leading-tight mb-4">
                Representation That<br />Prioritizes Results
              </h2>
              <p className="text-[#666] text-base leading-relaxed mb-10">
                At Gamble &amp; Garcia, we don&apos;t just practice law — we fight for outcomes. Here&apos;s what sets our firm apart from the rest.
              </p>
            </div>

            <div className="space-y-6">
              {reasons.map((r, i) => (
                <div
                  key={i}
                  className={`flex gap-5 group transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-[#111] rounded-sm group-hover:bg-[#C9A84C] transition-colors duration-300">
                    <i className={`${r.icon} text-[#C9A84C] group-hover:text-black text-lg transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#111] text-[15px] mb-1">{r.title}</h3>
                    <p className="text-[#666] text-sm leading-relaxed">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
